<template>
  <div
    id="story-board-content"
    class="page-no-break-before"
    :class="print_mode ? '' : 'vh-adjust px-lg-3 px-xl-5'"
  >
    <template v-if="disclosures === null">
      <div class="mb-5 vh-adjust">
        <div>
          <span class="mt-5"></span>
          <h5 class="mb-4 border-bottom-dark pb-2">
            <b-skeleton animation="wave" width="15%" />
            <b-skeleton animation="wave" width="30%" />
            <span class="gray-500 f-105 fw-400 ml-1">
              <b-skeleton animation="wave" width="20%" />
            </span>
          </h5>
        </div>

        <b-skeleton animation="fade" width="85%" />
        <b-skeleton animation="fade" width="55%" />
        <b-skeleton animation="fade" width="80%" />
        <b-skeleton animation="fade" width="30%" />
        <b-skeleton animation="fade" width="40%" />
        <br />
        <b-skeleton animation="fade" width="40%" />
        <b-skeleton animation="fade" width="20%" />
      </div>
    </template>

    <template v-else-if="disclosures.length > 0">
      <div
        v-for="(disclosure_group, group_idx) in groupedDisclosures"
        :id="
          !print_mode && disclosure_group.type !== 'documents'
            ? 'order_id_' + disclosure_group.order_index
            : ''
        "
        :key="'group_' + group_idx"
      >
        <!-- 그룹화된 문서 처리 -->
        <div
          v-if="disclosure_group.type === 'documents'"
          class="documents-group no-page-break-inside mb-4"
          :style="{ 'justify-content': disclosure_group.items[0].alignment }"
        >
          <DocumentSb
            v-for="(doc, doc_idx) in disclosure_group.items"
            :key="`document_${doc_idx}`"
            :print_mode="print_mode"
            :document_info="doc.data"
            :document_id="doc.data.id"
          />
        </div>

        <!-- 기타 타입 처리 -->
        <template v-else-if="!disclosure_group.guide_mode">
          <div
            v-if="disclosure_group.type === 'title'"
            :class="[
              disclosure_group.title_lv === 1 &&
              !(disclosure_group.title_lv === 1 && group_idx === 0)
                ? 'skinc-title-margin'
                : ''
            ]"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <Title
              v-if="lang === 'KOR'"
              :main_title="disclosure_group?.title1"
              :title_lv="disclosure_group?.title_lv"
              :link="disclosure_group?.link"
              :print_mode="print_mode"
              :is-first-lv1="disclosure_group.title_lv === 1 && group_idx === 0"
              :prev-title-lv="groupedDisclosures[group_idx - 1]?.title_lv || null"
            />

            <Title
              v-else
              :main_title="
                disclosure_group?.title1_em
                  ? disclosure_group?.title1_em
                  : disclosure_group?.title1_ea
              "
              :title_lv="disclosure_group?.title_lv"
              :link="disclosure_group?.link"
              :print_mode="print_mode"
              :is-first-lv1="disclosure_group.title_lv === 1 && group_idx === 0"
              :prev-title-lv="groupedDisclosures[group_idx - 1]?.title_lv || null"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'text'"
            class="mt-0 mb-4 text-box no-page-break-inside"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <mdsb v-if="lang === 'KOR'" :contents="disclosure_group.text" />

            <mdsb
              v-else
              :contents="
                disclosure_group.text_em ? disclosure_group.text_em : disclosure_group.text_ea
              "
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'image'"
            class="d-flex flex-column no-page-break-inside skinc-bottom-margin"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <ImageContent
              :disclosure-info="disclosure_group"
              :lang="lang"
              :show_title="disclosure_group.show_title"
              @image-loaded="onImageLoaded"
            />
          </div>

          <div v-else-if="disclosure_group.type === 'line'">
            <hr :style="disclosure_group.hr_style" />
          </div>

          <div
            v-else-if="disclosure_group.type === 'data_table'"
            class="no-page-break-inside skinc-bottom-margin"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <DataTableSb
              :data_table_data="disclosure_group.data"
              :print_mode="print_mode"
              table_mode="accordion"
              :show_title="disclosure_group.show_title"
              :force_lang="lang"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'chart'"
            class="no-page-break-inside skinc-bottom-margin"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <ChartCanvasSb
              :key="`chart_${disclosure_group.data.id}`"
              :chart_id="disclosure_group.data.id"
              :chart-data="disclosure_group.data.contents"
              :chart-data-e="disclosure_group.data.translated?.contents_e"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'business_case'"
            class="no-page-break-inside skinc-bottom-margin"
          >
            <BusinessCaseSb
              :business_case_obj="disclosure_group.data"
              :business_case_id="disclosure_group.data.id"
              business_case_mode="accordion"
              :show_title="disclosure_group.show_title"
              :background_color="disclosure_group.background_color"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'document'"
            class="mb-4"
            :style="{ 'justify-content': disclosure_group.items[0].alignment }"
          >
            <DocumentSb
              :document_info="disclosure_group.data"
              :document_id="disclosure_group.data.id"
            />
          </div>

          <div v-else-if="disclosure_group.type === 'document_set'" class="skinc-bottom-margin">
            <DocumentSetSb :disclosure_group="disclosure_group" :print_mode="print_mode" />
          </div>
        </template>
      </div>
    </template>

    <template v-else>
      <p>작성된 게시물이 없습니다.</p>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import DocumentSb from './DocumentSb.vue';
import DocumentSetSb from './DocumentSetSb.vue';
import DataTableSb from './DataTableSb.vue';
import BusinessCaseSb from './BusinessCaseSb.vue';
import ChartCanvasSb from './ChartCanvasSb.vue';
import Title from './Title.vue';
import ImageContent from './ImageContent.vue';

export default {
  name: 'Content',
  components: {
    DocumentSb,
    DataTableSb,
    BusinessCaseSb,
    ChartCanvasSb,
    Title,
    ImageContent,
    DocumentSetSb
  },
  props: {
    disclosures: { type: Array, default: null },
    print_mode: { type: Boolean, default: false },
    is_appendix: { type: Boolean, default: false }
  },
  watch: {
    disclosures: {
      immediate: true,
      handler(newV, oldV) {
        if (newV && JSON.stringify(newV) !== JSON.stringify(oldV)) {
          this.countImages();
          this.$nextTick(() => {
            this.moveScroll();
            setTimeout(() => {
              this.moveScroll(); // 즉시 스크롤  + 500ms 후 재시도로 SVG 렌더링 완료 후 정확한 위치로 보정
              this.highlightSearchKeyword(); // 검색어 하이라이트
            }, 500);
          });
        }
      }
    },
    '$route.query.q_str': {
      handler(newKeyword) {
        if (newKeyword) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.highlightSearchKeyword();
            }, 500);
          });
        } else {
          // q_str이 없으면 하이라이트 제거
          this.removeHighlights();
        }
      }
    },
    '$route.query.q_highlight': {
      handler(newKeyword) {
        if (newKeyword) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.highlightSearchKeyword();
            }, 500);
          });
        } else {
          this.removeHighlights();
        }
      }
    }
  },
  computed: {
    ...mapState('report_list', ['lang']),

    groupedDisclosures() {
      if (!this.disclosures) return [];

      // 1. guide_mode인 항목 모두 제거
      const filtered = this.disclosures.filter(item => !item.guide_mode);
      // 2. 제거 후, 첫 요소 타입이 'text'면 추가로 제거
      if (filtered.length > 0 && filtered[0].type === 'text') {
        filtered.shift();
      }
      const visibleDisclosures = filtered;

      const grouped = [];
      let currentGroup = [];

      visibleDisclosures.forEach((item, index) => {
        if (item.type === 'document') {
          currentGroup.push({ ...item, index });
        } else {
          if (currentGroup.length > 0) {
            grouped.push({ type: 'documents', items: [...currentGroup] });
            currentGroup = [];
          }
          grouped.push({ ...item, index });
        }
      });

      if (currentGroup.length > 0) {
        grouped.push({ type: 'documents', items: [...currentGroup] });
      }

      return grouped;
    }
  },
  data() {
    return {
      imageLoadCount: 0,
      totalImageCount: 0,
      scrollAttempted: false
    };
  },
  beforeDestroy() {
    // 컴포넌트 제거 시 하이라이트 제거
    this.removeHighlights();
  },
  methods: {
    highlightSearchKeyword() {
      const searchKeyword = this.$route.query.q_str || this.$route.query.q_highlight;
      if (!searchKeyword) {
        // 기존 하이라이트 제거
        this.removeHighlights();
        return;
      }

      // 기존 하이라이트 제거
      this.removeHighlights();

      const contentElement = document.getElementById('story-board-content');
      if (!contentElement) return;

      this.highlightTextNodes(contentElement, searchKeyword);
    },

    removeHighlights() {
      const highlights = document.querySelectorAll('.search-highlight-mark');
      highlights.forEach(mark => {
        const parent = mark.parentNode;
        parent.replaceChild(document.createTextNode(mark.textContent), mark);
        parent.normalize();
      });
    },

    highlightTextNodes(element, keyword) {
      const lowerKeyword = keyword.toLowerCase();
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function (node) {
            // 이미 하이라이트된 노드나 특정 태그는 제외
            if (
              node.parentElement.tagName === 'SCRIPT' ||
              node.parentElement.tagName === 'STYLE' ||
              node.parentElement.classList.contains('search-highlight-mark')
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      const nodesToProcess = [];
      let currentNode;
      while ((currentNode = walker.nextNode())) {
        if (currentNode.textContent.toLowerCase().includes(lowerKeyword)) {
          nodesToProcess.push(currentNode);
        }
      }

      nodesToProcess.forEach(node => {
        const text = node.textContent;
        const lowerText = text.toLowerCase();
        const fragments = [];
        let lastIndex = 0;

        let index = lowerText.indexOf(lowerKeyword);
        while (index !== -1) {
          // 앞부분 텍스트
          if (index > lastIndex) {
            fragments.push(document.createTextNode(text.substring(lastIndex, index)));
          }

          // 하이라이트할 텍스트
          const mark = document.createElement('mark');
          mark.className = 'search-highlight-mark';
          mark.textContent = text.substring(index, index + keyword.length);
          fragments.push(mark);

          lastIndex = index + keyword.length;
          index = lowerText.indexOf(lowerKeyword, lastIndex);
        }

        // 나머지 텍스트
        if (lastIndex < text.length) {
          fragments.push(document.createTextNode(text.substring(lastIndex)));
        }

        // 노드 교체
        if (fragments.length > 0) {
          const parent = node.parentNode;
          fragments.forEach(fragment => {
            parent.insertBefore(fragment, node);
          });
          parent.removeChild(node);
        }
      });
    },

    moveScroll() {
      const hash = this.$route.hash;
      if (hash) {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 90;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'auto'
          });
          this.scrollAttempted = true;
        } else {
          setTimeout(() => {
            this.moveScroll();
          }, 300);
        }
      }
    },
    onImageLoaded() {
      this.imageLoadCount++;
      if (this.imageLoadCount >= this.totalImageCount && this.scrollAttempted) {
        setTimeout(() => {
          this.moveScroll();
        }, 100);
      }
    },
    countImages() {
      this.totalImageCount = this.disclosures?.filter(item => item.type === 'image').length || 0;
      this.imageLoadCount = 0;
      this.scrollAttempted = false;
    }
  }
};
</script>

<style>
/* 검색어 하이라이트 스타일 */
.search-highlight-mark {
  background-color: #fff59d;
  color: #121212;
  padding: 2px 4px;
  border-radius: 2px;
  font-weight: 600;
}

.documents-group {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  /* 문서 간 간격 조정 */
}
</style>
